import { Routes, Route } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import Layout from './components/layout/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import RoleGuard from './components/auth/RoleGuard'

// Lazy-loaded pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const LiveAnalysis = lazy(() => import('./pages/LiveAnalysis'))
const Matches = lazy(() => import('./pages/Matches'))
const MatchDetail = lazy(() => import('./pages/MatchDetail'))
const Players = lazy(() => import('./pages/Players'))
const Analytics = lazy(() => import('./pages/Analytics'))
const Training = lazy(() => import('./pages/Training'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const Profile = lazy(() => import('./pages/Profile'))
const Settings = lazy(() => import('./pages/Settings'))
const Organization = lazy(() => import('./pages/Organization'))
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'))
const PlayerProfile = lazy(() => import('./pages/PlayerProfile'))
const Rankings = lazy(() => import('./pages/Rankings'))
const RankingDetail = lazy(() => import('./pages/RankingDetail'))
const Statistics = lazy(() => import('./pages/Statistics'))
const HeadToHead = lazy(() => import('./pages/HeadToHead'))
const Notifications = lazy(() => import('./pages/Notifications'))
const LiveScoring = lazy(() => import('./pages/LiveScoring'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'))
const AdminLogs = lazy(() => import('./pages/admin/AdminLogs'))
const AdminSystem = lazy(() => import('./pages/admin/AdminSystem'))
const AdminAudit = lazy(() => import('./pages/admin/AdminAudit'))
const AdminDatabaseSchema = lazy(() => import('./pages/admin/AdminDatabaseSchema'))
const Clubs = lazy(() => import('./pages/Clubs'))
const ClubCreate = lazy(() => import('./pages/ClubCreate'))
const ClubDetail = lazy(() => import('./pages/ClubDetail'))
const NewMatch = lazy(() => import('./pages/NewMatch'))
const LiveScore = lazy(() => import('./pages/LiveScore'))
const SpectatorScore = lazy(() => import('./pages/SpectatorScore'))
const MatchResult = lazy(() => import('./pages/MatchResult'))
const Tournaments = lazy(() => import('./pages/Tournaments'))
const TournamentDetail = lazy(() => import('./pages/TournamentDetail'))
const TournamentCreate = lazy(() => import('./pages/TournamentCreate'))

function App() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="loading-spinner" /></div>}>
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Perfil de jogador: exige login — consome as leituras de /api/players,
          agora autenticadas [TT-67392]. */}
      <Route path="/@:username" element={<ProtectedRoute><PlayerProfile /></ProtectedRoute>} />

      {/* Fullscreen PWA - live scoring (no layout wrapper). Exige login: o setup
          lista jogadores via /api/players, agora autenticada [TT-67392]. */}
      <Route path="/score" element={<ProtectedRoute><LiveScore /></ProtectedRoute>} />
      {/* Placar do espectador: só WebSocket de scoring, não lê /api/players — segue público. */}
      <Route path="/score/:matchId" element={<SpectatorScore />} />

      {/* Protected Pages (App) */}
      <Route element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="/app" element={<Dashboard />} />
        <Route path="/app/live" element={<LiveAnalysis />} />
        <Route path="/app/matches" element={<Matches />} />
        <Route path="/app/match/new" element={<NewMatch />} />
        <Route path="/app/match/result" element={<MatchResult />} />
        <Route path="/app/tournaments" element={<Tournaments />} />
        <Route path="/app/tournaments/new" element={<TournamentCreate />} />
        <Route path="/app/tournaments/:id" element={<TournamentDetail />} />
        <Route path="/app/clubs" element={<Clubs />} />
        <Route path="/app/clubs/new" element={<ClubCreate />} />
        <Route path="/app/clubs/:id" element={<ClubDetail />} />
        <Route path="/app/match/:id" element={<MatchDetail />} />
        <Route path="/app/match/:id/live" element={<LiveScoring />} />
        <Route path="/app/players" element={<Players />} />
        <Route path="/app/analytics" element={<Analytics />} />
        <Route path="/app/training" element={<Training />} />
        <Route path="/app/rankings" element={<Rankings />} />
        <Route path="/app/rankings/:id" element={<RankingDetail />} />
        <Route path="/app/profile" element={<Profile />} />
        <Route path="/app/settings" element={<Settings />} />
        <Route path="/app/organization" element={<Organization />} />
        <Route path="/app/accept-invite" element={<AcceptInvite />} />
        <Route path="/app/statistics" element={<Statistics />} />
        <Route path="/app/h2h" element={<HeadToHead />} />
        <Route path="/app/notifications" element={<Notifications />} />

        {/* Admin routes */}
        <Route path="/app/admin" element={
          <RoleGuard roles={['admin']}>
            <AdminDashboard />
          </RoleGuard>
        } />
        <Route path="/app/admin/users" element={
          <RoleGuard roles={['admin']}>
            <AdminUsers />
          </RoleGuard>
        } />
        <Route path="/app/admin/subscriptions" element={
          <RoleGuard roles={['admin']}>
            <AdminSubscriptions />
          </RoleGuard>
        } />
        <Route path="/app/admin/logs" element={
          <RoleGuard roles={['admin']}>
            <AdminLogs />
          </RoleGuard>
        } />
        <Route path="/app/admin/system" element={
          <RoleGuard roles={['admin']}>
            <AdminSystem />
          </RoleGuard>
        } />
        <Route path="/app/admin/audit" element={
          <RoleGuard roles={['admin']}>
            <AdminAudit />
          </RoleGuard>
        } />
        <Route path="/app/admin/database-schema" element={
          <RoleGuard roles={['admin']}>
            <AdminDatabaseSchema />
          </RoleGuard>
        } />
      </Route>
    </Routes>
    </Suspense>
  )
}

export default App
